import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Article } from '@/constants/articles'

import Image from 'next/image'
import Link from 'next/link'
import { Linkedin, Twitter, MessageCircle } from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { UnderlineLink } from '@/components/generals/underline-link'
import { Choose } from '@/components/home/choose'
import { siteConfig } from '@/constants/site'
import { articles, articleAuthor, getArticle } from '@/constants/articles'

interface ArticlePageProps {
    article: Article
    related: Article[]
}

const eyebrow = 'text-[12px] font-medium uppercase tracking-[0.16em] text-ink-soft'

const ArticlePage: FC<ArticlePageProps> = ({ article, related }): ReactNode => {
    const pageUrl = encodeURIComponent(`${siteConfig.url}/news/${article.slug}`)
    const pageTitle = encodeURIComponent(article.title)

    const shareLinks = [
        { icon: <MessageCircle className='size-4' />, label: "Share on WhatsApp", href: `https://wa.me/?text=${pageTitle}%20${pageUrl}` },
        { icon: <Twitter className='size-4' />, label: "Share on X", href: `https://twitter.com/intent/tweet?text=${pageTitle}&url=${pageUrl}` },
        { icon: <Linkedin className='size-4' />, label: "Share on LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}` }
    ]

    return (
        <Layout>
            <Seo title={article.seoTitle} description={article.description} />

            <section className='shell pt-28 md:pt-32 lg:pt-36'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <div data-reveal='up' data-reveal-load data-reveal-delay='0.4'>
                        <span className='inline-flex whitespace-nowrap rounded-full border border-hairline bg-white px-4 py-2 text-[16px] font-medium text-brand'>
                            {article.category}
                        </span>

                        <h1 className='t-h1 mt-6 max-w-[820px]'>
                            {article.title}
                        </h1>
                    </div>

                    <div className='shrink-0 lg:text-right' data-reveal='up' data-reveal-load data-reveal-delay='0.6'>
                        <p className={eyebrow}>
                            Season
                        </p>

                        <p className='t-label mt-2'>
                            {article.date}
                        </p>
                    </div>
                </div>
            </section>

            <section className='relative mt-10 h-[42vh] min-h-[300px] overflow-hidden md:mt-14 md:h-[66vh]'>
                <div className='absolute left-0 top-0 h-full w-full' data-parallax='30'>
                    <Image
                        src={article.image}
                        alt={article.imageAlt}
                        fill
                        sizes='100vw'
                        className='object-cover'
                        priority
                    />
                </div>
            </section>

            <section className='shell py-20 md:py-28'>
                <div className='grid gap-12 lg:grid-cols-[220px_minmax(0,1fr)_240px] lg:gap-14'>
                    <aside className='flex flex-col gap-10 lg:sticky lg:top-28 lg:self-start' data-reveal='up'>
                        {articleAuthor && (
                            <div>
                                <p className={eyebrow}>
                                    Written by
                                </p>

                                <div className='mt-4 flex items-center gap-3 rounded-[10px] border border-hairline bg-cream-soft p-3'>
                                    <span className='relative size-12 shrink-0 overflow-hidden rounded-full'>
                                        <Image
                                            src={articleAuthor.avatar}
                                            alt=''
                                            fill
                                            sizes='48px'
                                            className='object-cover'
                                        />
                                    </span>

                                    <span>
                                        <span className='block t-label'>
                                            {articleAuthor.name}
                                        </span>

                                        <span className='block text-[13px] text-ink-soft'>
                                            {articleAuthor.role}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}

                        <div>
                            <p className={eyebrow}>
                                Share on
                            </p>

                            <div className='mt-4 flex gap-2.5'>
                                {shareLinks.map((item) => (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={item.label}
                                        className='flex size-10 items-center justify-center rounded-full bg-brand text-cream transition-colors duration-300 hover:bg-ink'
                                    >
                                        {item.icon}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </aside>

                    <article className='border-hairline lg:border-x lg:px-12'>
                        {article.body.map((block, index) => (
                            block.kind === 'h2' ? (
                                <h2
                                    key={`${block.kind}-${index}`}
                                    className='t-h3 mb-5 mt-12 first:mt-0'
                                    data-reveal='up'
                                    data-reveal-distance='10'
                                >
                                    {block.text}
                                </h2>
                            ) : (
                                <p
                                    key={`${block.kind}-${index}`}
                                    className='t-body mb-5 leading-[1.6]'
                                    data-reveal='up'
                                    data-reveal-distance='10'
                                >
                                    {block.text}
                                </p>
                            )
                        ))}
                    </article>

                    <aside className='lg:sticky lg:top-28 lg:self-start' data-reveal='up'>
                        <p className={eyebrow}>
                            More articles
                        </p>

                        <ul className='mt-4 flex flex-col gap-2.5'>
                            {related.map((item) => (
                                <li key={item.slug}>
                                    <Link
                                        href={`/news/${item.slug}`}
                                        className='group flex items-center gap-3 rounded-[10px] border border-hairline bg-cream-soft p-2.5'
                                    >
                                        <span className='relative size-12 shrink-0 overflow-hidden rounded-[5px]'>
                                            <Image
                                                src={item.image}
                                                alt=''
                                                fill
                                                sizes='48px'
                                                className='object-cover transition-transform duration-500 group-hover:scale-110'
                                            />
                                        </span>

                                        <span className='line-clamp-2 font-heading text-[15px] leading-[1.2] text-brand'>
                                            {item.title}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-6 flex justify-end'>
                            <UnderlineLink href='/news' className='text-brand'>
                                View all
                            </UnderlineLink>
                        </div>
                    </aside>
                </div>
            </section>

            <Choose />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: articles.map((article) => ({ params: { slug: article.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({ params }) => {
    const article = getArticle(String(params?.slug))

    if (!article) return { notFound: true }

    const related = articles.filter((item) => item.slug !== article.slug).slice(0, 3)

    return { props: { article, related } }
}

export default ArticlePage
